import { Component, ReactNode } from 'react';
import ReactCrop, { PixelCrop, ReactCropProps } from 'react-image-crop';
import { AdminApi } from '../admin-api';
import LoadingButton from './LoadingButton';
const reduce = require('image-blob-reduce')();

export default function CropPhoto(
  props: Readonly<{
    children?: ReactNode;
    currentUser?: any;
    cardTitle: string;
    cardHelpText: string;
    uploaded: string | undefined;
    className: string | undefined;
    cropProps: Partial<ReactCropProps>;
    onRemove?: () => Promise<void>;
    onUpload: (key: string) => Promise<string>;
    noCard?: boolean;
  }>,
): ReactNode {
  return <CropPhotoComp {...props} />;
}

class CropPhotoComp extends Component {
  constructor(
    readonly props: Readonly<{
      children?: ReactNode;
      currentUser?: any;
      cardTitle: string;
      cropProps: Partial<ReactCropProps>;
      uploaded: string | undefined;
      cardHelpText: string;
      className: string | undefined;
      onRemove?: () => Promise<void>;
      onUpload: (key: string) => Promise<string>;
      noCard?: boolean;
    }>,
  ) {
    super(props);

    this.state = {
      crop: null,
      fileSrc: null,
      cropping: false,
      uploading: false,
    };
  }

  state: Readonly<{
    crop: PixelCrop | null;
    fileSrc: string | null;
    cropping: boolean;
    uploading: boolean;
  }>;

  changeFile(files: FileList | null) {
    if (!files) return;

    const _file = files[0];
    if (!_file) return;

    const reader = new FileReader();

    reader.addEventListener(
      'load',
      () => {
        this.setState({
          fileSrc: reader.result as string,
          cropping: true,
        });
      },
      false,
    );

    reader.readAsDataURL(_file);
  }

  setCrop(c: PixelCrop) {
    this.setState({
      crop: c,
    });
  }

  doCrop(cb: (blob: Blob) => void) {
    if (!this.state.crop) return;

    const _canvas = document.getElementById('cropped') as HTMLCanvasElement;
    const _ctx = _canvas.getContext('2d');

    const _toCrop = document.getElementById('to-crop') as HTMLImageElement;
    const _crop = this.state.crop;

    if (_ctx && _crop) {
      const scaleX = _toCrop.naturalWidth / _toCrop.width;
      const scaleY = _toCrop.naturalHeight / _toCrop.height;
      const pixelRatio = window.devicePixelRatio;

      _canvas.width = Math.floor(_crop.width * scaleX * pixelRatio);
      _canvas.height = Math.floor(_crop.height * scaleY * pixelRatio);

      _ctx.scale(pixelRatio, pixelRatio);
      _ctx.imageSmoothingQuality = 'medium';

      const cropX = _crop.x * scaleX;
      const cropY = _crop.y * scaleY;

      const centerX = _toCrop.naturalWidth / 2;
      const centerY = _toCrop.naturalHeight / 2;

      _ctx.save();

      _ctx.translate(-cropX, -cropY);
      _ctx.translate(centerX, centerY);

      _ctx.translate(-centerX, -centerY);
      _ctx.drawImage(
        _toCrop,
        0,
        0,
        _toCrop.naturalWidth,
        _toCrop.naturalHeight,
        0,
        0,
        _toCrop.naturalWidth,
        _toCrop.naturalHeight,
      );

      _ctx.restore();

      _canvas.toBlob((b) => {
        reduce.toBlob(b, { max: 1500 }).then((resized: Blob) => {
          cb(resized);
        });
      });
    }
  }

  cropContainer() {
    return (
      <ReactCrop
        crop={this.state.crop ?? undefined}
        {...this.props.cropProps}
        onChange={(c) => this.setCrop(c)}
      >
        <img id="to-crop" src={this.state.fileSrc!} />
      </ReactCrop>
    );
  }

  async removePhoto() {
    this.setState({
      uploading: true,
    });

    if (this.props.onRemove) {
      await this.props.onRemove!();
    }

    this.setState({
      uploading: false,
    });
  }

  async upload() {
    if (!this.state.crop) return;

    this.doCrop(async (blob) => {
      this.setState({
        uploading: true,
        cropping: false,
      });

      const _tempData = await AdminApi.post('/upload/temp-url', {});

      await fetch(_tempData.url, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': blob.type,
        },
      });

      await this.props.onUpload(_tempData.key);

      this.setState({
        uploading: false,
        fileSrc: null,
      });
    });
  }

  render(): ReactNode {
    return (
      <div className={this.props.noCard ? '' : 'card'}>
        <canvas id="cropped" style={{ display: 'none' }}></canvas>

        <div className={this.props.noCard ? '' : 'card-body'}>
          <h5 className="card-title">{this.props.cardTitle}</h5>
          <p className="card-text">{this.props.cardHelpText}</p>

          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                {this.props.uploaded ? (
                  <div className="text-center">
                    <img
                      src={this.props.uploaded}
                      className={this.props.className}
                    />
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => {
                          this.removePhoto();
                        }}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                ) : this.state.uploading ? (
                  <div className="text-center">
                    <LoadingButton isLarge={true} />
                  </div>
                ) : this.state.fileSrc ? (
                  this.cropContainer()
                ) : (
                  <input
                    className="form-control"
                    onChange={(e) => this.changeFile(e.target.files)}
                    accept="image/*"
                    type="file"
                    id="formFile"
                  />
                )}
              </div>
            </div>
          </div>
          {this.state.cropping && (
            <div className="row">
              <div className="col-md-6">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    this.setState({
                      cropping: false,
                      fileSrc: null,
                    });
                  }}
                >
                  Cancelar
                </button>
              </div>
              <div className="col-md-6">
                <button
                  disabled={!this.state.crop}
                  type="button"
                  className="btn btn-success btn-sm pull-right"
                  onClick={() => {
                    this.upload();
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
